import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { dummyEmployees, teamPerformance } from '../data/userManagement';
import { Users, TrendingUp, Target, Award, AlertCircle, Filter, Search } from 'lucide-react';

interface UserManagementProps {
  onBackToDashboard: () => void;
  onNavigate: (view: 'dashboard' | 'quotations' | 'analytics' | 'settings' | 'ai-assistant' | 'user-management') => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onBackToDashboard, onNavigate }) => {
  const [activeTab, setActiveTab] = useState('employees');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  const filteredEmployees = dummyEmployees.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || employee.status.toLowerCase() === statusFilter.toLowerCase();
    const matchesRole = roleFilter === 'all' || employee.role.toLowerCase() === roleFilter.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-gray-100 text-gray-800';
      case 'On Leave': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Admin': return 'bg-purple-100 text-purple-800';
      case 'Manager': return 'bg-blue-100 text-blue-800';
      case 'Sales Executive': return 'bg-green-100 text-green-800';
      case 'Customer Service': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const tabs = [
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'teams', label: 'Team Performance', icon: TrendingUp },
    { id: 'workload', label: 'Workload Distribution', icon: Target },
    { id: 'analytics', label: 'Performance Analytics', icon: Award }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar currentView="user-management" onNavigate={(view) => {
        if (view === 'dashboard') {
          onBackToDashboard();
        } else {
          onNavigate(view);
        }
      }} />

      {/* Main Content */}
      <div className="flex-1 ml-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
              <p className="text-gray-600 mt-1">Manage employees, track performance, and monitor team productivity</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {dummyEmployees.length} employees • {dummyEmployees.filter(e => e.status === 'Active').length} active
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'employees' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="card">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search employees..."
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="on leave">On Leave</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="sales executive">Sales Executive</option>
                      <option value="customer service">Customer Service</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Employees Table */}
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workload</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredEmployees.map((employee) => (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="text-2xl mr-3">{employee.avatar}</div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                                <div className="text-sm text-gray-500">{employee.email}</div>
                                <div className="text-xs text-gray-400">{employee.department}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(employee.role)}`}>
                              {employee.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(employee.status)}`}>
                              {employee.status}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              Last active: {new Date(employee.lastActive).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{employee.performance.winRate.toFixed(1)}%</span>
                                <span className="text-gray-500">win rate</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {employee.performance.avgResponseTime}h avg response
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{employee.currentWorkload.activeQuotations}</span>
                                <span className="text-gray-500">active</span>
                              </div>
                              {employee.currentWorkload.overdueQuotations > 0 && (
                                <div className="text-xs text-red-600 flex items-center">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  {employee.currentWorkload.overdueQuotations} overdue
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              ${employee.performance.totalRevenue.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">
                              ${employee.performance.avgDealValue.toLocaleString()} avg
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <span className="text-gray-500">—</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'teams' && (
            <div className="space-y-6">
              {/* Team Performance Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {teamPerformance.map((team, index) => (
                  <div key={index} className="card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{team.teamName}</h3>
                      <div className="text-2xl">👥</div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Members:</span>
                        <span className="text-sm font-medium">{team.activeMembers}/{team.totalMembers}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Win Rate:</span>
                        <span className="text-sm font-medium">{team.winRate.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Avg Response:</span>
                        <span className="text-sm font-medium">{team.avgResponseTime.toFixed(1)}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Revenue:</span>
                        <span className="text-sm font-medium">${team.totalRevenue.toLocaleString()}</span>
                      </div>
                      <div className="mt-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Monthly Target</span>
                          <span>{team.monthlyAchievement}/{team.monthlyTarget}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full" 
                            style={{ width: `${(team.monthlyAchievement / team.monthlyTarget) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Team Performance Table */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance Summary</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Members</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quotations</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Win Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target Progress</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {teamPerformance.map((team, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{team.teamName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{team.activeMembers}/{team.totalMembers}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{team.totalQuotations}</div>
                            <div className="text-xs text-gray-500">{team.wonQuotations} won</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              team.winRate >= 70 ? 'bg-green-100 text-green-800' :
                              team.winRate >= 60 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {team.winRate.toFixed(1)}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">${team.totalRevenue.toLocaleString()}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-primary-600 h-2 rounded-full" 
                                  style={{ width: `${(team.monthlyAchievement / team.monthlyTarget) * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600">
                                {Math.round((team.monthlyAchievement / team.monthlyTarget) * 100)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'workload' && (
            <div className="space-y-6">
              {/* Workload Overview */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Active</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dummyEmployees.reduce((sum, emp) => sum + emp.currentWorkload.activeQuotations, 0)}
                      </p>
                    </div>
                    <div className="text-2xl">📋</div>
                  </div>
                </div>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Overdue Tasks</p>
                      <p className="text-2xl font-bold text-red-600">
                        {dummyEmployees.reduce((sum, emp) => sum + emp.currentWorkload.overdueQuotations, 0)}
                      </p>
                    </div>
                    <div className="text-2xl">⚠️</div>
                  </div>
                </div>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Today's Tasks</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {dummyEmployees.reduce((sum, emp) => sum + emp.currentWorkload.todayTasks, 0)}
                      </p>
                    </div>
                    <div className="text-2xl">📅</div>
                  </div>
                </div>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">This Week</p>
                      <p className="text-2xl font-bold text-green-600">
                        {dummyEmployees.reduce((sum, emp) => sum + emp.currentWorkload.thisWeekTasks, 0)}
                      </p>
                    </div>
                    <div className="text-2xl">📊</div>
                  </div>
                </div>
              </div>

              {/* Workload Distribution Table */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Workload Distribution</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Quotations</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overdue</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Today's Tasks</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">This Week</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workload Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dummyEmployees.filter(emp => emp.status === 'Active').map((employee) => {
                        const totalWorkload = employee.currentWorkload.activeQuotations + employee.currentWorkload.todayTasks;
                        const workloadStatus = totalWorkload > 20 ? 'High' : totalWorkload > 10 ? 'Medium' : 'Low';
                        const statusColor = workloadStatus === 'High' ? 'bg-red-100 text-red-800' : 
                                          workloadStatus === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 
                                          'bg-green-100 text-green-800';
                        
                        return (
                          <tr key={employee.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="text-xl mr-3">{employee.avatar}</div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                                  <div className="text-sm text-gray-500">{employee.role}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{employee.currentWorkload.activeQuotations}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-sm font-medium ${employee.currentWorkload.overdueQuotations > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                {employee.currentWorkload.overdueQuotations}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{employee.currentWorkload.todayTasks}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{employee.currentWorkload.thisWeekTasks}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                {workloadStatus}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* Performance Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Win Rate</p>
                      <p className="text-2xl font-bold text-green-600">
                        {(dummyEmployees.reduce((sum, emp) => sum + emp.performance.winRate, 0) / dummyEmployees.length).toFixed(1)}%
                      </p>
                    </div>
                    <div className="text-2xl">🎯</div>
                  </div>
                </div>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {(dummyEmployees.reduce((sum, emp) => sum + emp.performance.avgResponseTime, 0) / dummyEmployees.length).toFixed(1)}h
                      </p>
                    </div>
                    <div className="text-2xl">⏱️</div>
                  </div>
                </div>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                      <p className="text-2xl font-bold text-purple-600">
                        ${dummyEmployees.reduce((sum, emp) => sum + emp.performance.totalRevenue, 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-2xl">💰</div>
                  </div>
                </div>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Customer Satisfaction</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {(dummyEmployees.reduce((sum, emp) => sum + emp.performance.customerSatisfaction, 0) / dummyEmployees.length).toFixed(1)}/5
                      </p>
                    </div>
                    <div className="text-2xl">⭐</div>
                  </div>
                </div>
              </div>

              {/* Top Performers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">🏆 Top Performers</h3>
                  <div className="space-y-3">
                    {dummyEmployees
                      .filter(emp => emp.status === 'Active')
                      .sort((a, b) => b.performance.winRate - a.performance.winRate)
                      .slice(0, 5)
                      .map((employee, index) => (
                        <div key={employee.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="text-lg">{employee.avatar}</div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                              <div className="text-xs text-gray-500">{employee.role}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">{employee.performance.winRate.toFixed(1)}%</div>
                            <div className="text-xs text-gray-500">win rate</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">⚡ Fastest Responders</h3>
                  <div className="space-y-3">
                    {dummyEmployees
                      .filter(emp => emp.status === 'Active')
                      .sort((a, b) => a.performance.avgResponseTime - b.performance.avgResponseTime)
                      .slice(0, 5)
                      .map((employee, index) => (
                        <div key={employee.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="text-lg">{employee.avatar}</div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                              <div className="text-xs text-gray-500">{employee.role}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">{employee.performance.avgResponseTime.toFixed(1)}h</div>
                            <div className="text-xs text-gray-500">avg response</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
